import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { showToast } from '@/shared/utils/showToast';
import { useAuthStore } from '@/store/authStore';
import {
  cancelOrder,
  checkout,
  fetchOrder,
  fetchOrders,
  fetchOrderTracking,
  finishOrder,
  reportTransfer,
} from '../apis/order';
import type { OrderDisplayStatus, TransferStatus } from '../types/order.types';
import { isNotFound } from './useProducts';

const PAGE_SIZE = 10;

// Per user, so one account's orders never show up for the next on the same browser
const useOrdersKey = () => ['orders', useAuthStore((state) => state.user_id)];

/** Purchase history pages for a status filter (undefined = every status) and search term. */
export const useOrderPages = (
  status: OrderDisplayStatus | undefined,
  q = '',
  transferStatuses?: TransferStatus[]
) => {
  const isLogin = useAuthStore((state) => state.isLogin);
  const transferStatus = transferStatuses?.join(',');
  return useInfiniteQuery({
    queryKey: [...useOrdersKey(), status ?? 'all', transferStatus ?? 'all', q],
    queryFn: ({ pageParam }) =>
      fetchOrders({ page: pageParam, size: PAGE_SIZE, status, transfer_status: transferStatus, q }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.total_pages ? last.page + 1 : undefined),
    // Keep the current list on screen while another filter or search loads
    placeholderData: keepPreviousData,
    enabled: isLogin,
  });
};

/** Lacak Pengiriman of one order. A tracked parcel may have moved the order on (e.g. to Dikirim), so the
 *  history reloads with it. */
export const useOrderTracking = (orderId: string | undefined) => {
  const isLogin = useAuthStore((state) => state.isLogin);
  const userId = useAuthStore((state) => state.user_id);
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ['order-tracking', userId, orderId],
    queryFn: async () => {
      const tracking = await fetchOrderTracking(orderId as string);
      queryClient.invalidateQueries({ queryKey: ['orders', userId] });
      return tracking;
    },
    enabled: isLogin && !!orderId,
    // A missing or someone else's order does not appear on a retry
    retry: (failureCount, error) => !isNotFound(error) && failureCount < 3,
  });
};

/** Bayar Sekarang. The bought lines leave the cart and the order joins the history, so both reload,
 *  also after a failure (e.g. a cart changed meanwhile). */
export const useCheckout = () => {
  const queryClient = useQueryClient();
  const userId = useAuthStore((state) => state.user_id);
  const reload = () => {
    queryClient.invalidateQueries({ queryKey: ['cart', userId] });
    queryClient.invalidateQueries({ queryKey: ['orders', userId] });
  };
  return useMutation({
    mutationFn: checkout,
    onSuccess: reload,
    onError: (error) => {
      reload();
      showToast({ type: 'error', title: 'Gagal membuat pesanan', message: error.message });
    },
  });
};

/** Batalkan: cancels an unpaid order, then reloads every history page. */
export const useCancelOrder = () => {
  const queryClient = useQueryClient();
  const queryKey = useOrdersKey();
  return useMutation({
    mutationFn: cancelOrder,
    onSuccess: (order) => {
      queryClient.invalidateQueries({ queryKey });
      showToast({
        type: 'success',
        title: 'Pesanan dibatalkan',
        message: `Pesanan ${order.order_number} dibatalkan.`,
      });
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey });
      showToast({ type: 'error', title: 'Gagal membatalkan pesanan', message: error.message });
    },
  });
};

/** Pembayaran: one order with its transfer instructions; nothing is requested without an id. */
export const useOrder = (orderId: string | undefined) => {
  const isLogin = useAuthStore((state) => state.isLogin);
  return useQuery({
    queryKey: [...useOrdersKey(), 'detail', orderId],
    queryFn: () => fetchOrder(orderId as string),
    enabled: isLogin && Boolean(orderId),
  });
};

/** Saya sudah transfer: the admin is alerted to check the statement of the account picked; every
 *  history page and the order reload. */
export const useReportTransfer = () => {
  const queryClient = useQueryClient();
  const queryKey = useOrdersKey();
  return useMutation({
    mutationFn: ({ orderId, bankAccountId }: { orderId: string; bankAccountId: string }) =>
      reportTransfer(orderId, bankAccountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      showToast({
        type: 'success',
        title: 'Transfer dilaporkan',
        message: 'Pembayaran Anda sedang diverifikasi oleh admin.',
      });
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey });
      showToast({ type: 'error', title: 'Gagal melaporkan transfer', message: error.message });
    },
  });
};

/** Diterima: completes the order, then reloads every history page. */
export const useFinishOrder = () => {
  const queryClient = useQueryClient();
  const queryKey = useOrdersKey();
  return useMutation({
    mutationFn: finishOrder,
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey });
      showToast({ type: 'error', title: 'Gagal menyelesaikan pesanan', message: error.message });
    },
  });
};
